import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { api } from '../api/client'
import type { SoftwareMap as SoftwareMapData, SoftwareMapComponent } from '../api/types'
import { BottoniEsporta } from '../components/BottoniEsporta'
import { CategoryBars, Donut } from '../components/charts'
import { Card, EmptyState, ErrorBanner, Spinner, timeAgo } from '../components/common'
import { DataTable } from '../components/DataTable'
import type { Column } from '../components/DataTable'
import { LinkKev } from '../components/Kev'
import { etichettaDipendenza, kindLabel, nomeApplicazione, nonLicenziato, NotaLicenzaSbom, ScaricaCycloneDxApplicazione, TabellaAvvisi } from '../components/Sbom'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'

/**
 * Application Maintenance (CENSO SBOM; it was «Mappa software»), in the
 * «Inventario Software» group, in tabs: «Panoramica» (the dashboard:
 * counters, severity, ecosystems, licences, servers), «Applicazioni»,
 * «Componenti» (every library grouped by version, with where it is) and
 * «Librerie vulnerabili» (with the advisories). Licences have their own page
 * (Licenze dei software). Every table filters and sorts per column (DataTable). The tab
 * is in the URL (?vista=), so a view can be shared. See docs/architecture/sbom.md.
 */

type Vista = 'panoramica' | 'applicazioni' | 'componenti' | 'vulnerabili'

const VISTE: Array<{ key: Vista; label: () => string }> = [
  { key: 'panoramica', label: () => t('Panoramica') },
  { key: 'applicazioni', label: () => t('Applicazioni') },
  { key: 'componenti', label: () => t('Componenti') },
  { key: 'vulnerabili', label: () => t('Librerie vulnerabili') },
]

const n = (x: number) => x.toLocaleString(localeCorrente())

export default function SoftwareMap() {
  const [params, setParams] = useSearchParams()
  const vista = (VISTE.find((v) => v.key === params.get('vista'))?.key ?? 'panoramica') as Vista
  const dati = useAsync(() => api.softwareMap(), [])

  const header = (
    <header className="page__header">
      <div>
        <h1 className="page__title">{t('Application Maintenance')}</h1>
        <p className="page__subtitle">
          {t('censisce software, componenti e librerie installati sui server tramite agenti CENSO o importazione CycloneDX/SPDX. Processi, porte in ascolto e traffico osservato correlano i componenti software alle dipendenze applicative, supportando il registro DORA e l’identificazione degli asset interessati da una vulnerabilità.')}
        </p>
      </div>
      <BottoniEsporta nome="censo-application-maintenance" disabled={!dati.data} path={api.softwareMapExportPath} />
    </header>
  )

  if (dati.error && nonLicenziato(dati.error)) {
    return <>{header}<Card><NotaLicenzaSbom /></Card></>
  }

  return (
    <>
      {header}
      <nav className="tabs" role="tablist">
        {VISTE.map((v) => (
          <button key={v.key} role="tab" aria-selected={vista === v.key}
            className={`tabs__tab${vista === v.key ? ' tabs__tab--active' : ''}`}
            onClick={() => setParams(v.key === 'panoramica' ? {} : { vista: v.key })}>
            {v.label()}
          </button>
        ))}
      </nav>
      <ErrorBanner error={dati.error} />
      {dati.loading && !dati.data ? <Spinner /> : !dati.data || dati.data.applications.length === 0 ? (
        <Card>
          <EmptyState
            title={t('Nessuna applicazione ancora')}
            hint={t('Accendi «SBOM» negli agenti CENSO dei server (Discovery → Agenti d’inventario → Impostazioni): alla consegna successiva le applicazioni compaiono qui. In alternativa importa un documento CycloneDX o SPDX dalla sezione «Software (SBOM)» di un CI.')}
          />
        </Card>
      ) : (
        <>
          {vista === 'panoramica' && <Panoramica dati={dati.data} vai={(v) => setParams({ vista: v })} />}
          {vista === 'applicazioni' && <Applicazioni dati={dati.data} />}
          {vista === 'componenti' && <Componenti />}
          {vista === 'vulnerabili' && <Vulnerabili dati={dati.data} />}
        </>
      )}
    </>
  )
}

function Stat({ valore, etichetta, hint, tono, onClick }: {
  valore: number; etichetta: string; hint?: string; tono?: 'warn' | 'error' | 'ok'; onClick?: () => void
}) {
  const contenuto = (
    <>
      <div className="stat__value">{n(valore)}</div>
      <div className="stat__label">{etichetta}</div>
      {hint && <div className="stat__hint">{hint}</div>}
    </>
  )
  const cls = `stat${tono ? ` stat--${tono}` : ''}`
  return onClick ? <button className={cls} onClick={onClick}>{contenuto}</button> : <div className={cls}>{contenuto}</div>
}

function Panoramica({ dati, vai }: { dati: SoftwareMapData; vai: (v: Vista) => void }) {
  const navigate = useNavigate()
  const s = dati.stats
  const sev = s.severity
  return (
    <>
      <div className="stats">
        <Stat valore={s.servers} etichetta={t('Server')} />
        <Stat valore={s.applications} etichetta={t('Applicazioni')} onClick={() => vai('applicazioni')}
          hint={s.latest_update ? t('ultimo aggiornamento {quando}', { quando: timeAgo(s.latest_update) }) : undefined} />
        <Stat valore={s.components} etichetta={t('Componenti')} onClick={() => vai('componenti')}
          hint={t('{n} librerie diverse', { n: n(s.distinct_components) })} />
        <Stat valore={s.vulnerable_libraries} etichetta={t('Librerie vulnerabili')} tono={s.vulnerable_libraries ? 'warn' : 'ok'}
          onClick={() => vai('vulnerabili')} />
        <Stat valore={s.exploited} etichetta={t('Sfruttate attivamente (CISA KEV)')} tono={s.exploited ? 'error' : 'ok'}
          onClick={() => vai('vulnerabili')} />
        <Stat valore={s.licenses_distinct} etichetta={t('Licenze diverse')} onClick={() => navigate('/software-licenses')} />
        <Stat valore={s.system_packages} etichetta={t('Software di sistema')} hint={t('pacchetti della distribuzione')} />
        <Stat valore={s.extra_packages} etichetta={t('Software extra sistema')} hint={t('terze parti, scompattati, percorsi personalizzati')} />
      </div>

      <div className="grid grid--2">
        <Card title={t('Librerie vulnerabili per gravità')}>
          <Donut
            centreLabel={t('librerie')}
            slices={[
              { key: 'critica', label: t('critica'), value: sev.critica, color: 'var(--error)' },
              { key: 'alta', label: t('alta'), value: sev.alta, color: 'var(--warn)' },
              { key: 'media', label: t('media'), value: sev.media, color: 'var(--accent)' },
              { key: 'bassa', label: t('bassa'), value: sev.bassa, color: 'var(--text-muted)' },
            ]}
            onSliceClick={() => vai('vulnerabili')}
          />
        </Card>
        <Card title={t('Dipendenze dirette e transitive')}>
          <Donut
            centreLabel={t('componenti')}
            slices={[
              { key: 'direct', label: t('dirette'), value: s.direct, color: 'var(--accent)' },
              { key: 'transitive', label: t('transitive'), value: s.transitive, color: 'var(--warn)' },
              { key: 'unknown', label: t('non note'), value: s.unknown_scope, color: 'var(--text-muted)' },
            ]}
          />
        </Card>
        <Card title={t('Ecosistemi')}>
          <CategoryBars data={Object.entries(dati.ecosystems).map(([k, v]) => ({ key: k, label: k, value: v }))} />
        </Card>
        <Card title={t('Licenze più usate')}>
          <CategoryBars data={Object.entries(dati.licenses).map(([k, v]) => ({
            key: k, label: k === '—' ? t('Non dichiarata') : k, value: v }))} />
        </Card>
        <Card title={t('Applicazioni per tipo')}>
          <CategoryBars data={Object.entries(s.applications_by_kind).map(([k, v]) => ({ key: k, label: kindLabel(k), value: v }))} />
        </Card>
        <Card title={t('Server con più librerie vulnerabili')}>
          <CategoryBars data={s.servers_detail.filter((x) => x.vulnerable > 0).slice(0, 10)
            .map((x) => ({ key: x.ci_id, label: x.ci_name, value: x.vulnerable }))}
            emptyHint={t('Nessun server con librerie vulnerabili.')} />
        </Card>
      </div>

      <Card title={t('Server')}>
        <DataTable
          rows={s.servers_detail}
          rowKey={(r) => r.ci_id}
          initialSort={{ key: 'vulnerable', dir: 'desc' }}
          conteggio={{ nome: t('server') }}
          paginazione={{ predefinita: 25 }}
          columns={[
            { key: 'ci_name', header: t('Server'), render: (r) => <Link to={`/cis/${r.ci_id}#sbom`}>{r.ci_name}</Link> },
            { key: 'applications', header: t('Applicazioni'), value: (r) => r.applications, filter: false },
            { key: 'components', header: t('Componenti'), value: (r) => r.components, filter: false },
            { key: 'vulnerable', header: t('Vulnerabili'), value: (r) => r.vulnerable, filter: false,
              render: (r) => (r.vulnerable ? <span className="badge badge--warn">{r.vulnerable}</span> : '—') },
            { key: 'system', header: t('Software di sistema'), value: (r) => r.system, filter: false },
            { key: 'extra', header: t('Software extra sistema'), value: (r) => r.extra, filter: false },
          ]}
        />
      </Card>
    </>
  )
}

function Applicazioni({ dati }: { dati: SoftwareMapData }) {
  type Riga = SoftwareMapData['applications'][number]
  const [errore, setErrore] = useState<unknown>(null)
  const colonne: Column<Riga>[] = [
    { key: 'ci_name', header: t('Server'), render: (a) => <Link to={`/cis/${a.ci_id}#sbom`}>{a.ci_name}</Link> },
    { key: 'name', header: t('Applicazione'), value: (a) => nomeApplicazione(a),
      render: (a) => <>{nomeApplicazione(a)}{a.source === 'import' && <small className="text-muted"> · {t('importata')}</small>}</> },
    { key: 'kind', header: t('Tipo'), value: (a) => kindLabel(a.kind) },
    { key: 'ports', header: t('Porte'), value: (a) => a.ports.join(', '), mono: true },
    { key: 'process', header: t('Processo'), mono: true },
    { key: 'components', header: t('Componenti'), value: (a) => a.components, filter: false },
    { key: 'vulnerable', header: t('Vulnerabili'), value: (a) => a.vulnerable, filter: false,
      render: (a) => (a.vulnerable > 0 ? <span className="badge badge--warn">{a.vulnerable}</span> : '—') },
    { key: 'updated_at', header: t('Aggiornata'), value: (a) => a.updated_at ?? '', filter: false,
      render: (a) => <small>{a.updated_at ? timeAgo(a.updated_at) : '—'}</small> },
    { key: 'cyclonedx', header: '', filter: false, sort: false, nowrap: true,
      render: (a) => (a.components > 0 ? <ScaricaCycloneDxApplicazione app={a} ciName={a.ci_name} onErrore={setErrore} /> : null) },
  ]
  return (
    <Card>
      <ErrorBanner error={errore} />
      <DataTable rows={dati.applications} rowKey={(a) => a.id} columns={colonne}
        initialSort={{ key: 'vulnerable', dir: 'desc' }} conteggio={{ nome: t('applicazioni') }}
        paginazione={{ predefinita: 50, opzioni: [25, 50, 100, 250] }} />
    </Card>
  )
}

type DoveSiRiga = { ci_id: string; ci_name: string; application: string; path: string; scope: string }

function DoveSi({ c }: { c: SoftwareMapComponent }) {
  const dove = useAsync(() => api.softwareMapWhere(c.ecosystem, c.name, c.version), [c.ecosystem, c.name, c.version])
  if (dove.loading && !dove.data) return <Spinner />
  const colonne: Column<DoveSiRiga>[] = [
    { key: 'ci_name', header: t('Server'), render: (d) => <Link to={`/cis/${d.ci_id}#sbom`}>{d.ci_name}</Link> },
    { key: 'application', header: t('Applicazione') },
    { key: 'scope', header: t('Dipendenza'), value: (d) => etichettaDipendenza(d.scope) },
    { key: 'path', header: t('Percorso'), mono: true, render: (d) => <small>{d.path || '—'}</small> },
  ]
  return (
    <div className="card__inset">
      <ErrorBanner error={dove.error} />
      <DataTable rows={dove.data ?? []} columns={colonne} rowKey={(d, i) => `${d.ci_id}:${d.path}:${i}`}
        conteggio={{ nome: t('server') }} />
    </div>
  )
}

function Componenti() {
  const [cerca, setCerca] = useState('')
  const [applicata, setApplicata] = useState('')
  const [soloVulnerabili, setSoloVulnerabili] = useState(false)
  const dati = useAsync(() => api.softwareMapComponents({ q: applicata, vulnerable: soloVulnerabili }), [applicata, soloVulnerabili])
  const colonne: Column<SoftwareMapComponent>[] = [
    { key: 'name', header: t('Componente'), mono: true },
    { key: 'version', header: t('Versione'), mono: true },
    { key: 'ecosystem', header: t('Ecosistema') },
    { key: 'scope', header: t('Dipendenza'), value: (c) => etichettaDipendenza(c.scope) },
    { key: 'licenses', header: t('Licenze'), value: (c) => c.licenses.join(', ') },
    { key: 'servers', header: t('Server'), value: (c) => c.servers, filter: false },
    { key: 'applications', header: t('Applicazioni'), value: (c) => c.applications, filter: false },
    { key: 'vulnerabilities', header: t('Vulnerabilità'), value: (c) => c.vulnerabilities, filter: false,
      render: (c) => (c.vulnerabilities ? <span className="badge badge--warn">{c.vulnerabilities}</span> : '—') },
  ]
  return (
    <Card>
      <form className="toolbar" onSubmit={(e) => { e.preventDefault(); setApplicata(cerca) }}>
        <input type="search" value={cerca} onChange={(e) => setCerca(e.target.value)}
          placeholder={t('Cerca una libreria in tutti i server (es. log4j, openssl, lodash)')} />
        <button type="submit" className="secondary">{t('Cerca')}</button>
        <label className="check">
          <input type="checkbox" checked={soloVulnerabili} onChange={(e) => setSoloVulnerabili(e.target.checked)} />{' '}
          {t('Solo vulnerabili')}
        </label>
      </form>
      <ErrorBanner error={dati.error} />
      {dati.loading && !dati.data ? <Spinner /> : dati.data && (
        <>
          {dati.data.truncated && (
            <p className="field__hint">{t('Mostrati i primi {m} di {n}, per numero di server: cerca per nome per restringere.', { m: n(dati.data.components.length), n: n(dati.data.total) })}</p>
          )}
          <DataTable rows={dati.data.components} rowKey={(c) => `${c.ecosystem}:${c.name}@${c.version}`} columns={colonne}
            initialSort={{ key: 'servers', dir: 'desc' }} conteggio={{ nome: t('componenti') }}
            paginazione={{ predefinita: 50, opzioni: [25, 50, 100, 250] }}
            expand={(c) => <DoveSi c={c} />} />
        </>
      )}
    </Card>
  )
}

function Vulnerabili({ dati }: { dati: SoftwareMapData }) {
  type Riga = SoftwareMapData['vulnerabilities']['components'][number]
  const colonne: Column<Riga>[] = [
    { key: 'name', header: t('Componente'), mono: true,
      render: (c) => <><span className="mono">{c.name}</span>{c.exploited && <> <LinkKev cve={c.detail.find((d) => d.exploited)?.cves[0]} /></>}</> },
    { key: 'version', header: t('Versione'), mono: true },
    { key: 'ecosystem', header: t('Ecosistema') },
    { key: 'dependency', header: t('Dipendenza'), value: (c) => etichettaDipendenza(c.dependency) },
    { key: 'cis', header: t('Server'), value: (c) => c.cis, filter: false },
    { key: 'advisories', header: t('Avvisi'), value: (c) => c.advisories, filter: false },
    { key: 'cvss', header: 'CVSS', value: (c) => c.cvss ?? '', filter: false },
    { key: 'fixed', header: t('Corretta in'), value: (c) => c.fixed ?? '', mono: true },
  ]
  return (
    <Card>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Ogni libreria vulnerabile è anche un rischio di sicurezza sul CI che la contiene («Libreria vulnerabile»), con la versione che la corregge. Una dipendenza transitiva non è dichiarata dall’applicazione: si corregge aggiornando la libreria che la porta o forzandone la versione.')}
      </p>
      <DataTable rows={dati.vulnerabilities.components} rowKey={(c) => `${c.ecosystem}:${c.name}@${c.version}`} columns={colonne}
        initialSort={{ key: 'cvss', dir: 'desc' }} conteggio={{ nome: t('librerie') }}
        paginazione={{ predefinita: 50 }}
        emptyText={t('Nessuna libreria vulnerabile.')}
        expand={(c) => (
          <div className="card__inset">
            <p style={{ marginTop: 0 }}>
              {t('Su')}{' '}
              {c.ci_list.map((ci, i) => <span key={ci.id}>{i > 0 && ', '}<Link to={`/cis/${ci.id}#sbom`}>{ci.name}</Link></span>)}
              {c.cis > c.ci_list.length && ` ${t('e altri {n}', { n: c.cis - c.ci_list.length })}`}
            </p>
            <TabellaAvvisi vulnerabilita={c.detail} />
          </div>
        )} />
    </Card>
  )
}
